import { normalize } from './dates';
import { cpfDigits, normalizeEmail, phoneDigits } from './members';
import type { Member } from './types';

/**
 * Quem da planilha já está cadastrado? Cada ministério manda a sua lista, e a mesma
 * pessoa aparece em várias — às vezes escrita de outro jeito ("Maria do P. Socorro" ×
 * "Maria do Perpétuo Socorro", "Andréa" × "Andrea", um sobrenome a menos). A importação
 * compara cada linha com os cadastros e oferece "É a mesma pessoa": em vez de duplicar o
 * cadastro, a linha só acrescenta o ministério (e preenche o que estava vazio).
 *
 * A semelhança é só uma sugestão, sempre conferida na tela: `certain` (mesmo nome, e-mail,
 * CPF ou telefone), `likely` e `possible`.
 */

export type MatchLevel = 'certain' | 'likely' | 'possible';

export interface MemberMatch {
  memberId: string;
  /** Semelhança do nome, de 0 a 1 (1 com e-mail, CPF ou telefone iguais). */
  score: number;
  level: MatchLevel;
  /** Por que parece a mesma pessoa, para ler: "Mesmo nome", "Mesmo telefone", "Nome quase igual · 92%". */
  reasons: string[];
}

/** Daqui para cima, provavelmente a mesma pessoa (já vem marcada na tela). */
export const MATCH_LIKELY = 0.88;
/** Daqui para cima, talvez — aparece para conferir, marcada como outra pessoa. */
export const MATCH_POSSIBLE = 0.75;
/** No máximo tantos cadastros sugeridos por linha. */
export const MAX_MATCHES = 3;

/** Partículas que não distinguem ninguém. */
const PARTICLES = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'di', 'du', 'del']);

/** Os sufixos de família escritos de mais de um jeito ("Júnior", "Jr."). */
const SUFFIXES: Record<string, string> = {
  junior: 'jr',
  jr: 'jr',
  filho: 'fo',
  fo: 'fo',
  neto: 'nt',
  nt: 'nt',
};

/** As palavras do nome que contam: sem acento, maiúscula, pontuação nem partículas. */
export function nameTokens(name: string): string[] {
  return normalize(name)
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t && !PARTICLES.has(t))
    .map((t) => SUFFIXES[t] ?? t);
}

/** Jaro-Winkler: 1 = igual; pondera mais o começo (o que mais se repete entre grafias). */
export function jaroWinkler(a: string, b: string): number {
  if (a === b) return 1;
  if (!a || !b) return 0;

  const range = Math.max(0, Math.floor(Math.max(a.length, b.length) / 2) - 1);
  const aHit = Array<boolean>(a.length).fill(false);
  const bHit = Array<boolean>(b.length).fill(false);
  let matches = 0;

  for (let i = 0; i < a.length; i++) {
    for (let j = Math.max(0, i - range); j < Math.min(b.length, i + range + 1); j++) {
      if (bHit[j] || a[i] !== b[j]) continue;

      aHit[i] = true;
      bHit[j] = true;
      matches++;
      break;
    }
  }

  if (!matches) return 0;

  let k = 0;
  let swaps = 0;

  for (let i = 0; i < a.length; i++) {
    if (!aHit[i]) continue;
    while (!bHit[k]) k++;
    if (a[i] !== b[k]) swaps++;
    k++;
  }

  const jaro = (matches / a.length + matches / b.length + (matches - swaps / 2) / matches) / 3;
  let prefix = 0;

  while (prefix < 4 && a[prefix] === b[prefix]) prefix++;

  return jaro + prefix * 0.1 * (1 - jaro);
}

/** Uma palavra contra outra: igual, abreviada ("P." × "Perpétuo") ou escrita parecida. */
function tokenScore(short: string, long: string): number {
  if (short === long) return 1;
  if (short.length === 1 || long.length === 1) return short[0] === long[0] ? 0.8 : 0;

  const jw = jaroWinkler(short, long);

  return jw >= 0.9 ? jw : 0;
}

/**
 * Semelhança de dois nomes, de 0 a 1. Cada palavra do nome mais curto casa com a melhor do
 * mais longo (sem repetir); o primeiro nome tem de casar com o primeiro (senão, no máximo
 * 0,5), e o que sobra do nome mais longo pesa pouco — sobrenome a menos ainda parece a
 * mesma pessoa, sobrenomes diferentes não. Nome de uma palavra só, ou de duas dentro de um
 * mais longo ("Ana Silva" × "Ana Paula Silva"), fica no máximo em "possível": é pouco para
 * juntar cadastros sem conferir.
 */
export function nameSimilarity(a: string, b: string): number {
  const x = nameTokens(a);
  const y = nameTokens(b);

  if (!x.length || !y.length) return 0;
  if (x.join(' ') === y.join(' ')) return 1;

  const [short, long] = x.length <= y.length ? [x, y] : [y, x];
  const first = tokenScore(short[0]!, long[0]!);
  const used = new Set<number>([0]);
  let total = first;

  for (const token of short.slice(1)) {
    let best = 0;
    let at = -1;

    long.forEach((other, i) => {
      if (used.has(i)) return;

      const score = tokenScore(token, other);

      if (score > best) {
        best = score;
        at = i;
      }
    });

    if (at >= 0) used.add(at);
    total += best;
  }

  // Cada palavra do mais longo que ficou sem par tira um pouco.
  const leftover = long.length - used.size;
  let score = (total / short.length) * (1 - 0.04 * leftover);

  if (first < 0.9) score = Math.min(score, 0.5);
  if (short.length === 1 || (short.length === 2 && leftover > 0))
    score = Math.min(score, MATCH_LIKELY - 0.01);

  return Math.max(0, Math.min(1, score));
}

/** O que a linha traz para comparar. */
export interface MatchProbe {
  name: string;
  nickname?: string;
  email?: string;
  phone?: string;
  cpf?: string;
}

/**
 * Os cadastros que parecem a pessoa da linha, do mais parecido ao menos (até
 * `MAX_MATCHES`). E-mail, CPF ou telefone iguais são `certain` mesmo com o nome diferente.
 */
export function findMatches(probe: MatchProbe, members: readonly Member[]): MemberMatch[] {
  const email = normalizeEmail(probe.email ?? '');
  const cpf = cpfDigits(probe.cpf ?? '');
  const phone = phoneDigits(probe.phone ?? '');
  const nickname = normalize((probe.nickname ?? '').trim());

  return members
    .flatMap((m): MemberMatch[] => {
      const reasons: string[] = [];
      const same = (value: string, other: string, label: string) => {
        if (value && value === other) reasons.push(label);
      };

      same(email, normalizeEmail(m.email), 'Mesmo e-mail');
      same(cpf, m.cpf, 'Mesmo CPF');
      same(phone, m.phone, 'Mesmo telefone');

      const contact = reasons.length > 0;
      const similarity = nameSimilarity(probe.name, m.name);

      if (similarity === 1) reasons.unshift('Mesmo nome');
      else if (similarity >= MATCH_POSSIBLE)
        reasons.unshift(`Nome parecido · ${Math.round(similarity * 100)}%`);
      if (nickname && nickname === normalize(m.nickname.trim()) && (contact || similarity >= MATCH_POSSIBLE))
        reasons.push('Mesmo nome de apresentação');

      const score = contact ? 1 : similarity;

      if (score < MATCH_POSSIBLE) return [];

      return [{ memberId: m.id, score, level: levelOf(score, contact), reasons }];
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_MATCHES);
}

function levelOf(score: number, contact: boolean): MatchLevel {
  if (contact || score === 1) return 'certain';

  return score >= MATCH_LIKELY ? 'likely' : 'possible';
}
